import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, MenuItem, Skeleton, Stack,
  TextField, Typography,
} from '@mui/material';
import { useMenuContext } from '../../../../../context/menuContext';
import Authentication from '../../../../../utils/authentication';
import { enterTabletPairingView } from '../../../../../utils/tabletPairingPrivacy';
import { LOGIN_PATH } from '../../../../../constants/routes';
import { createOperationId, revocationOperations } from '../../../../../services/TabletPairingService';
import { useTabletPairing } from './useTabletPairing';
import { styles } from './TabletPairing.styles';

const pairingSignInPath = `${LOGIN_PATH}?reauth=tablet-management`;

export function formatTime(value) {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toLocaleString();
}

export function nativeLabel(entity, type = 'User', boundId = entity?.id) {
  const fallback = `${type} ${boundId || '—'}`;
  if (!entity || String(entity.id) !== String(boundId) || entity.display_status === 'unavailable') return `${fallback} · Name unavailable`;
  const label = type === 'Restaurant' ? entity.name : entity.display_name;
  if (typeof label !== 'string' || !label.trim()) return `${fallback} · Name unavailable`;
  return `${label} · ${fallback}${entity.display_status === 'retained' ? ' · Last-known label' : ' · Current label'}`;
}

export function NativeDisplay({ entity, type = 'User', boundId = entity?.id }) {
  return (
    <Box>
      <Typography variant="body2">{nativeLabel(entity, type, boundId)}</Typography>
      {entity?.display_status === 'retained' && (
        <Typography sx={styles.metadata}>Label last observed: {formatTime(entity.display_observed_at)}. Not evidence of current access.</Typography>
      )}
    </Box>
  );
}

function ErrorDetails({ error, retryIn = 0 }) {
  if (!error) return null;
  let guidance = 'Review the request before trying again.';
  if (error.status === 401 || error.code === 'HUMAN_AUTHENTICATION_REQUIRED') guidance = 'Sign in intentionally to continue tablet management. Other portal state has not been cleared.';
  else if (error.code === 'MANAGEMENT_FORBIDDEN' || error.status === 403) guidance = 'You do not currently have authority for this action. Ask a restaurant administrator; requests will not be retried automatically.';
  else if (error.status === 404) guidance = 'This resource is no longer available. Refresh the relevant list.';
  else if (error.status === 429) guidance = 'Too many requests. Wait before retrying the same operation.';
  else if (error.status === 503) guidance = 'The service or authorization check is unavailable. Existing enrollment is unchanged. Retry after the indicated delay.';
  else if (error.status === 409) guidance = 'The operation conflicts with current state. Do not change a recovery request or silently replace its operation.';
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <Typography variant="body2">{error.message}</Typography>
      <Typography variant="body2">{guidance}</Typography>
      <Typography sx={styles.metadata}>Code: {error.code} · Recovery: {error.recovery_action}</Typography>
      {error.request_id && <Typography sx={styles.metadata}>Request reference: {error.request_id}</Typography>}
      {!!error.retryAfter && <Typography variant="body2">Retry-After: {error.retryAfter} seconds. {retryIn > 0 ? `Wait ${retryIn} seconds.` : 'You may retry now.'}</Typography>}
      {(error.status === 401 || error.code === 'HUMAN_AUTHENTICATION_REQUIRED') && <Button component="a" href={pairingSignInPath} color="inherit">Go to sign in</Button>}
    </Alert>
  );
}

function CollectionState({ page, kind, onRetry, disabled }) {
  if (page.loading && !page.items.length) return <Box aria-label={`Loading ${kind}`}><Skeleton height={54} /><Skeleton height={54} /></Box>;
  if (page.error) return <Box sx={{ my: 2 }}><Typography variant="body2">Could not load {kind}. See the request details above.</Typography><Button onClick={onRetry} disabled={disabled}>Retry {kind}</Button></Box>;
  if (page.loaded && !page.items.length) return <Box sx={{ py: 3 }}><Typography variant="subtitle2">{kind === 'eligible users' ? 'No eligible users found' : kind === 'devices' ? 'No enrolled tablets yet' : 'No audit events yet'}</Typography><Typography variant="body2" color="text.secondary">{kind === 'eligible users' ? 'An enabled existing user needs access to this restaurant. An App User designation is not required.' : kind === 'devices' ? 'Issue a code above and enter it on a tablet to enroll it.' : 'Restaurant-scoped management and device events will appear here.'}</Typography></Box>;
  return null;
}

const reasons = {
  administrator_requested: 'Administrator requested',
  lost_device: 'Lost device',
  security_response: 'Security response',
  replaced_device: 'Replaced device',
};

function DeviceDialog({ dialog, busy, disabled, error, retryIn, onClose, onSave }) {
  const [name, setName] = useState(dialog.device.display_name);
  const [reason, setReason] = useState(dialog.plan?.reason || 'administrator_requested');
  const [confirmed, setConfirmed] = useState(false);
  const revoke = dialog.kind === 'revoke';
  const validName = !!name.trim() && Array.from(name.trim()).length <= 64;
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="tablet-device-dialog-title">
      <Box component="form" onSubmit={(event) => { event.preventDefault(); onSave(revoke ? reason : name.trim()); }}>
        <DialogTitle id="tablet-device-dialog-title">{revoke ? 'Permanently revoke tablet?' : 'Rename tablet'}</DialogTitle>
        <DialogContent>
          <ErrorDetails error={error} retryIn={retryIn} />
          <DialogContentText sx={{ mb: 2 }}>{revoke ? 'This enrollment cannot be restored. The tablet will need a new pairing. Other tablets and personal sign-in are not affected.' : 'Change only the tablet nickname. Its restaurant and associated user stay fixed.'}</DialogContentText>
          <Typography variant="subtitle2">{dialog.device.display_name}</Typography>
          <Typography sx={styles.metadata}>Device {dialog.device.id}</Typography>
          <NativeDisplay entity={dialog.device.associated_user} />
          {revoke ? (
            <>
              <TextField select fullWidth margin="normal" label="Revocation reason" value={reason} disabled={busy || !!dialog.plan} onChange={(event) => setReason(event.target.value)}>
                {Object.entries(reasons).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              {dialog.plan && <Alert severity="info" sx={{ my: 2 }}>A revocation response was not confirmed. Retry the same reason and operation; a replacement request will not be created.</Alert>}
              <Box component="label" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2, cursor: 'pointer' }}>
                <input type="checkbox" checked={confirmed} disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} />
                <Typography variant="body2" component="span">I understand this permanently revokes this tablet.</Typography>
              </Box>
            </>
          ) : <TextField autoFocus fullWidth margin="normal" label="Tablet nickname" value={name} onChange={(event) => setName(event.target.value)} disabled={busy} helperText="1–64 characters. This is not the native user label." error={!validName} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Close</Button>
          <Button type="submit" variant="contained" color={revoke ? 'error' : 'primary'} disabled={busy || disabled || (revoke ? !confirmed : !validName)}>{busy ? 'Saving…' : revoke ? dialog.plan ? 'Retry revocation' : 'Revoke tablet permanently' : 'Save nickname'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function actorLabel(actor) {
  return actor ? `${actor.type}: ${actor.id}` : 'Unknown — no verified historical identity';
}

export function TabletPairingPanel({ restaurantId, scope }) {
  useEffect(() => enterTabletPairingView(), []);
  const model = useTabletPairing(restaurantId, scope);
  const { pages, operation, pairing, busy, retryIn, blocked, now } = model;
  const [selectedUser, setSelectedUser] = useState('');
  const [nickname, setNickname] = useState('');
  const [confirmNew, setConfirmNew] = useState(false);
  const [dialog, setDialog] = useState(null);
  const disabled = busy || retryIn > 0 || blocked;
  const validName = !!nickname.trim() && Array.from(nickname.trim()).length <= 64;
  const selectedExists = pages.users.items.some((user) => user.id === selectedUser);
  const canIssue = !disabled && model.recoveryReady && selectedExists && validName;
  const showCode = pairing?.state === 'pending' && pairing.code && model.visible && now < Date.parse(pairing.expires_at);
  const requestNew = () => {
    if (operation || pairing?.state === 'pending') setConfirmNew(true);
    else model.issue({ associated_user_id: selectedUser, display_name: nickname.trim() });
  };
  const saveDevice = async (value) => {
    if (dialog.kind === 'rename') {
      if (await model.rename(dialog.device.id, value)) setDialog(null);
    } else {
      // Read and persist before sending, including after a reload/navigation.
      // Uncertain responses retain the original logical body and operation key.
      try {
        let plan = revocationOperations.read(scope, dialog.device.id);
        if (!plan) {
          try {
            plan = { key: createOperationId(), reason: value };
          } catch (_) {
            model.report({ code: 'SECURE_RANDOMNESS_UNAVAILABLE', message: 'A secure operation could not be created. Use a secure, supported browser.' });
            return;
          }
          revocationOperations.write(scope, dialog.device.id, plan);
        }
        setDialog((old) => ({ ...old, plan }));
        if (await model.revoke(dialog.device.id, plan.reason, plan.key)) {
          // Only a confirmed successful response permits clearing this record.
          revocationOperations.clear(scope, dialog.device.id);
          setDialog(null);
        }
      } catch (_) {
        model.report({ code: 'RECOVERY_STORAGE_UNAVAILABLE', message: 'Revocation recovery storage is unavailable or invalid. Restore session storage before retrying; no replacement operation was created.' });
      }
    }
  };
  const openRevocation = (device) => {
    try {
      const plan = revocationOperations.read(scope, device.id);
      setDialog({ kind: 'revoke', device, plan });
    } catch (_) {
      model.report({ code: 'RECOVERY_STORAGE_UNAVAILABLE', message: 'Revocation recovery storage is unavailable or invalid. Restore session storage before retrying; no replacement operation was created.' });
    }
  };
  const hasPendingRevocation = (device) => {
    try { return !!revocationOperations.read(scope, device.id); }
    catch (_) { return false; } // Opening an active target reports storage errors explicitly.
  };
  const pagination = (name, label) => pages[name].next_cursor && (
    <Button sx={{ mt: 1 }} disabled={disabled || pages[name].loading} onClick={() => model.load(name, pages[name].next_cursor)}>
      {pages[name].loading ? 'Loading…' : `Load more ${label}`}
    </Button>
  );
  return (
    <Box sx={styles.root} aria-busy={busy}>
      <Box sx={styles.header}>
        <Typography variant="h5" component="h1" gutterBottom>Tablet management</Typography>
        <Typography variant="body2" color="text.secondary">Pair restaurant tablets without sharing a personal password. Restaurant App Setup and existing personal sign-in remain available.</Typography>
        <Typography sx={styles.metadata}>Restaurant {restaurantId} · Management authority is checked by the API for each action.</Typography>
      </Box>
      <ErrorDetails error={model.error} retryIn={retryIn} />
      {model.notice && <Alert severity="success" sx={{ mb: 2 }}>{model.notice}</Alert>}
      <Box sx={styles.panel} component="section" aria-labelledby="pair-tablet-title">
        <Typography id="pair-tablet-title" variant="h6" component="h2">Pair a tablet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Select an enabled existing user with access to this restaurant. Their current restaurant permissions govern this tablet. Future permission changes affect every tablet associated with that user.</Typography>
        {operation && (
          <Alert severity="info" sx={{ mt: 2 }} action={<Button color="inherit" disabled={disabled || !model.recoveryReady || !model.visible} onClick={() => model.issue(null, true)}>Recover same code</Button>}>
            A previous issuance can be recovered using its original request: User {operation.body.associated_user_id}, tablet “{operation.body.display_name}”. Recovery does not extend its original expiry or create a new code.
          </Alert>
        )}
        <Box component="form" onSubmit={(event) => { event.preventDefault(); if (canIssue) requestNew(); }}>
          <Box sx={styles.fields}>
            <TextField select label="Associated user" value={selectedUser} disabled={disabled || pages.users.loading} onChange={(event) => setSelectedUser(event.target.value)} fullWidth helperText="App User is an informational designation, not an eligibility rule.">
              {pages.users.items.map((user) => <MenuItem value={user.id} key={user.id}>{nativeLabel(user)} · {user.app_user === true ? 'App User' : user.app_user === false ? 'Not designated App User' : 'App User designation unknown'}</MenuItem>)}
            </TextField>
            <TextField label="Tablet nickname" value={nickname} disabled={disabled} onChange={(event) => setNickname(event.target.value)} fullWidth error={!!nickname && !validName} helperText="A separate nickname, 1–64 characters. For example, Front counter." />
          </Box>
          <CollectionState page={pages.users} kind="eligible users" disabled={busy || retryIn > 0} onRetry={() => model.load('users', null, true)} />
          <Box sx={styles.row}>
            <Button type="submit" variant="contained" disabled={!canIssue}>{busy ? 'Working…' : 'Issue new code'}</Button>
            <Button disabled={busy || retryIn > 0 || pages.users.loading} onClick={() => model.load('users', null, true)}>Refresh eligible users</Button>
            {pagination('users', 'users')}
          </Box>
        </Box>
        {pairing && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }} aria-label="Pairing status">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Code status</Typography>
              <Chip size="small" label={pairing.state} color={pairing.state === 'redeemed' ? 'success' : 'default'} />
              {pairing.recovered && <Chip size="small" variant="outlined" label="Recovered operation" />}
            </Stack>
            <Typography variant="body2">Tablet nickname: {pairing.display_name}</Typography>
            <NativeDisplay type="Restaurant" entity={pairing.restaurant} boundId={restaurantId} />
            <NativeDisplay entity={pairing.associated_user} boundId={pairing.associated_user_id || operation?.body.associated_user_id || pairing.associated_user?.id} />
            {showCode ? (
              <>
                <Typography sx={styles.code} aria-label="One-use pairing code">{pairing.code}</Typography>
                <Typography variant="body2">Enter this one-use code on the tablet. Keep it private.</Typography>
              </>
            ) : pairing.state === 'pending' && <Alert severity="info" sx={{ my: 2 }}>The code has been hidden. Recover the same operation above to reveal it intentionally.</Alert>}
            <Typography sx={styles.metadata}>Original expiry: {formatTime(pairing.expires_at)}</Typography>
            {pairing.device_id && <Typography sx={styles.metadata}>Resulting device: {pairing.device_id}</Typography>}
            <Box sx={{ ...styles.row, mt: 2 }}>
              {showCode && <Button disabled={busy} onClick={model.closeCode}>Hide code</Button>}
              {pairing.state === 'pending' && <Button color="error" disabled={disabled} onClick={model.cancel}>Cancel code</Button>}
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={styles.panel} component="section" aria-labelledby="tablet-devices-title">
        <Box sx={{ ...styles.row, justifyContent: 'space-between', mb: 1 }}>
          <Typography id="tablet-devices-title" variant="h6" component="h2">Enrolled tablets</Typography>
          <Button disabled={busy || retryIn > 0 || pages.devices.loading} onClick={() => model.load('devices', null, true)}>Refresh devices</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Enrollment and current access are separate. Last activity is not an online indicator. Blocked-user tablets remain manageable by authorized managers.</Typography>
        <CollectionState page={pages.devices} kind="devices" disabled={busy || retryIn > 0} onRetry={() => model.load('devices', null, true)} />
        {pages.devices.items.map((device) => (
          <Box key={device.id} sx={styles.record} component="article" aria-label={`Tablet ${device.display_name}`}>
            <Box sx={{ ...styles.row, justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle1" component="h3">{device.display_name}</Typography>
                <Typography sx={styles.metadata}>Tablet nickname · Device {device.id}</Typography>
              </Box>
              <Box sx={styles.row}>
                <Chip size="small" label={`Enrollment: ${device.enrollment_status}`} variant="outlined" />
                <Chip size="small" label={`Access: ${device.access_state}`} color={device.access_state === 'allowed' ? 'success' : device.access_state === 'blocked' ? 'warning' : 'default'} />
              </Box>
            </Box>
            <Box sx={{ my: 1.5 }}>
              <NativeDisplay entity={device.restaurant} type="Restaurant" boundId={restaurantId} />
              <NativeDisplay entity={device.associated_user} />
              <Typography sx={styles.metadata}>App User designation: {device.associated_user?.app_user === null ? 'Unknown' : device.associated_user?.app_user ? 'Yes' : 'No'}</Typography>
            </Box>
            <Typography variant="body2">Last activity: {formatTime(device.last_seen_at)}</Typography>
            <Typography sx={styles.metadata}>{device.platform} · App {device.app_version} · OS {device.os_version}</Typography>
            <Typography sx={styles.metadata}>Enrolled: {formatTime(device.created_at)} · Access checked: {formatTime(device.access_checked_at)}</Typography>
            {device.access_reason && <Typography variant="body2">Access reason: {device.access_reason}</Typography>}
            {device.enrollment_status === 'revoked' && <Typography variant="body2">Permanently revoked: {formatTime(device.revoked_at)} · Reason: {device.revoked_reason || 'Not recorded'}</Typography>}
            <Box sx={{ ...styles.row, mt: 1.5 }}>
              <Button size="small" disabled={disabled} onClick={() => setDialog({ kind: 'rename', device })}>Rename</Button>
              <Button size="small" color="error" disabled={disabled || (device.enrollment_status === 'revoked' && !hasPendingRevocation(device))} onClick={() => openRevocation(device)}>{device.enrollment_status === 'revoked' && hasPendingRevocation(device) ? 'Recover revocation' : 'Revoke'}</Button>
            </Box>
          </Box>
        ))}
        {pagination('devices', 'devices')}
      </Box>
      <Box sx={styles.panel} component="section" aria-labelledby="tablet-audit-title">
        <Box sx={{ ...styles.row, justifyContent: 'space-between', mb: 1 }}>
          <Typography id="tablet-audit-title" variant="h6" component="h2">Management audit</Typography>
          <Button disabled={busy || retryIn > 0 || pages.audit.loading} onClick={() => model.load('audit', null, true)}>Refresh audit</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Merged API and Auth Hub history. The event actor is distinct from the original pairing issuer. Unknown historical identities are not inferred.</Typography>
        <CollectionState page={pages.audit} kind="audit history" disabled={busy || retryIn > 0} onRetry={() => model.load('audit', null, true)} />
        {pages.audit.items.map((event) => (
          <Box component="article" aria-label={`Audit ${event.id}`} key={event.id} sx={styles.record}>
            <Box sx={{ ...styles.row, justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" component="h3">{event.action}</Typography>
              <Chip label={event.outcome} size="small" variant="outlined" />
            </Box>
            <Typography sx={styles.metadata}>{formatTime(event.occurred_at)} · {event.source} · Event {event.id}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>Event actor: {actorLabel(event.event_actor)}</Typography>
            <Typography variant="body2">Original pairing issuer: {actorLabel(event.pairing_issuer)}</Typography>
            <Typography variant="body2">Associated user: {event.associated_user_id ? `User ${event.associated_user_id}` : 'Not recorded'}</Typography>
            <Typography sx={styles.metadata}>Restaurant {event.restaurant_id} · Device: {event.device_id || 'Not recorded'} · Pairing: {event.pairing_id || 'Not recorded'}</Typography>
            <Typography sx={styles.metadata}>Reason: {event.reason || 'Not recorded'} · Request reference: {event.request_id}</Typography>
          </Box>
        ))}
        {pagination('audit', 'events')}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary">To change a tablet’s restaurant or associated user, revoke it and pair it again. Renaming never changes its identity bindings.</Typography>
      {dialog && <DeviceDialog key={`${dialog.kind}:${dialog.device.id}`} dialog={dialog} busy={busy} disabled={disabled} error={model.error} retryIn={retryIn} onClose={() => setDialog(null)} onSave={saveDevice} />}
      <Dialog open={confirmNew} onClose={() => setConfirmNew(false)} aria-labelledby="new-pairing-title">
        <DialogTitle id="new-pairing-title">Issue a different code?</DialogTitle>
        <DialogContent><DialogContentText>This starts a new issuance with a new operation. It does not recover or cancel the previous code. Any previous pending code stays valid until canceled or expired. Cancel it first if it should no longer be used.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmNew(false)}>Keep previous operation</Button>
          <Button variant="contained" disabled={!canIssue} onClick={() => { setConfirmNew(false); model.issue({ associated_user_id: selectedUser, display_name: nickname.trim() }); }}>Issue different code</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function TabletPairing() {
  const { selectedRestaurant } = useMenuContext();
  const user = useSelector((state) => state.user.user);
  const [resolved, setResolved] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const restaurantId = selectedRestaurant?.id ? String(selectedRestaurant.id) : null;
  useEffect(() => {
    let active = true;
    setResolved(null);
    if (user && restaurantId) {
      Promise.resolve().then(() => Authentication.getPairingSessionScope()).then((session) => {
        if (active) setResolved({ user, restaurantId, session, attempt });
      }).catch(() => {
        if (active) setResolved({ user, restaurantId, session: null, attempt, failed: true });
      });
    }
    return () => { active = false; };
  }, [user, restaurantId, attempt]);
  useEffect(() => {
    const recheckSession = () => {
      // Native storage events are outside React's event batching in React 17.
      // Clear the old panel first, before starting any asynchronous digest read.
      setResolved(null);
      setAttempt((old) => old + 1);
    };
    const resume = () => {
      if (document.visibilityState !== 'hidden') recheckSession();
    };
    window.addEventListener('storage', recheckSession);
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.removeEventListener('storage', recheckSession);
      document.removeEventListener('visibilitychange', resume);
    };
  }, []);
  if (!restaurantId) return <Alert severity="info">Select a restaurant to manage tablets.</Alert>;
  if (!user) return <Alert severity="warning">A human sign-in is required. <Button component="a" href={pairingSignInPath}>Go to sign in</Button></Alert>;
  // Synchronous identity comparison prevents even a single render of old scoped
  // data while the asynchronous session check for a new Redux identity runs.
  if (!resolved || resolved.user !== user || resolved.restaurantId !== restaurantId || resolved.attempt !== attempt) return <Box aria-label="Checking tablet management session"><Skeleton height={70} /><Skeleton height={180} /></Box>;
  if (!resolved.session) return <Alert severity={resolved.failed ? 'error' : 'warning'}>{resolved.failed ? 'The secure tablet management session check failed. No management requests were sent. Check the session again or use a secure, supported browser.' : 'A verified human session is required before tablet management can load.'} <Button onClick={() => setAttempt((old) => old + 1)}>Check session again</Button><Button component="a" href={pairingSignInPath}>Go to sign in</Button></Alert>;
  const scope = JSON.stringify([String(user.id), resolved.session, restaurantId]);
  return <TabletPairingPanel key={`${scope}:${attempt}`} scope={scope} restaurantId={restaurantId} />;
}

export default TabletPairing;
